import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider
} from '@mui/material';
import MailIcon from '@mui/icons-material/Mail';
import LockIcon from '@mui/icons-material/Lock';
import PasswordField from '../components/PasswordField';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 25%, #1976d2 50%, #42a5f5 75%, #90caf9 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
          top: '-50px',
          left: '-100px',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
          bottom: '-100px',
          right: '-50px',
        }
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper 
          elevation={20} 
          sx={{ 
            p: 5, 
            width: '100%',
            borderRadius: 4,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
            border: '1px solid rgba(25, 118, 210, 0.1)',
            backdropFilter: 'blur(10px)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative top border */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #1976d2, #42a5f5, #90caf9)',
            }}
          />

          {/* Logo and Brand Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 2,
              pb: 4,
            }}
          >
            {/* M Circle Logo with Animation */}
            <Box
              sx={{
                position: 'relative',
                mb: 3,
              }}
            >
              {/* Outer glow effect */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  opacity: 0.2,
                  filter: 'blur(20px)',
                }}
              />
              
              {/* Main Logo */}
              <Box
                sx={{
                  position: 'relative',
                  width: 90,
                  height: 90,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 12px 30px rgba(25, 118, 210, 0.35), inset 0 1px 0 rgba(255,255,255,0.2)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  '&:hover': {
                    transform: 'scale(1.08)',
                    boxShadow: '0 18px 40px rgba(25, 118, 210, 0.45), inset 0 1px 0 rgba(255,255,255,0.3)',
                  }
                }}
              >
                <Typography
                  sx={{
                    fontSize: '56px',
                    fontWeight: 800,
                    color: '#ffffff',
                    fontFamily: 'Roboto, sans-serif',
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  M
                </Typography>
              </Box>
            </Box>
            
            {/* Macronix Text */}
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 900,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: 2.5,
                mb: 0.3,
              }}
            >
              MACRONIX
            </Typography>
            
            {/* Subtitle */}
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#666',
                fontSize: '13px',
                fontWeight: 500,
                letterSpacing: 0.5,
              }}
            >
              DOCUMENT VALIDATION SYSTEM
            </Typography>
          </Box>

          {/* Divider */}
          <Divider 
            sx={{ 
              mb: 3,
              background: 'linear-gradient(90deg, transparent, rgba(25,118,210,0.2), transparent)'
            }} 
          />

          {/* Login Form Section */}
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ 
              mb: 0.5,
              color: '#333',
              fontWeight: 700,
              fontSize: '18px',
            }}
          >
            Super Admin Login
          </Typography>

          <Typography 
            variant="body2" 
            align="center" 
            sx={{ 
              mb: 3,
              color: '#999',
              fontSize: '13px',
            }}
          >
            Access your secure administration portal
          </Typography>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                backgroundColor: '#ffebee',
                border: '1px solid #ffcdd2'
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">
            <Box sx={{ mb: 2.5 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                name="email"
                autoComplete="off"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailIcon sx={{ color: '#1976d2', fontSize: '22px' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    backgroundColor: '#f8f9ff',
                    transition: 'all 0.3s ease-in-out',
                    '& fieldset': {
                      borderColor: 'rgba(25, 118, 210, 0.2)',
                      borderWidth: '1.5px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    opacity: 0.7,
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <PasswordField
                label="Password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="off"
                email={email}
                showValidation={false}
              />
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.8,
                fontSize: '16px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                boxShadow: '0 8px 20px rgba(25, 118, 210, 0.3)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                borderRadius: 2.5,
                '&:hover:not(:disabled)': {
                  boxShadow: '0 12px 32px rgba(25, 118, 210, 0.45)',
                  transform: 'translateY(-2px)',
                  background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                },
                '&:active:not(:disabled)': {
                  transform: 'translateY(0px)',
                },
                '&:disabled': {
                  background: '#e0e0e0',
                  boxShadow: 'none',
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </form>

          {/* Footer */}
          <Box 
            sx={{ 
              textAlign: 'center', 
              pt: 3,
              borderTop: '1px solid rgba(25, 118, 210, 0.1)'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#bbb', 
                fontSize: '11px',
                fontWeight: 500,
                letterSpacing: 0.5
              }}
            >
              🔒 SECURE LOGIN PORTAL
            </Typography>
          </Box>
        </Paper>

        {/* Bottom decorative element */}
        <Box
          sx={{
            textAlign: 'center',
            mt: 3,
            opacity: 0.7,
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
            © 2026 Macronix. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;